import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  CalendarDays,
  CheckSquare,
  Mail,
  Phone,
  StickyNote,
} from "lucide-react";

import type {
  CrmChatRecordContext,
  ManagingPromptAction,
  ManagingPromptActionType,
} from "../types/chat.types";
import { openManagingPromptAction } from "../utils/managingPromptAction";

export interface ChatPromptActionsProps {
  actions: ManagingPromptAction[];
  record?: CrmChatRecordContext | null;
  disabled?: boolean;
}

function iconForAction(type: ManagingPromptActionType): React.ReactNode {
  const cls = "h-3.5 w-3.5";
  switch (type) {
    case "send-email":
      return <Mail className={cls} aria-hidden />;
    case "create-task":
      return <CheckSquare className={cls} aria-hidden />;
    case "create-note":
      return <StickyNote className={cls} aria-hidden />;
    case "schedule-meeting":
      return <CalendarDays className={cls} aria-hidden />;
    case "schedule-call":
      return <Phone className={cls} aria-hidden />;
    default:
      return null;
  }
}

const ChatPromptActions: React.FC<ChatPromptActionsProps> = ({
  actions,
  record,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  if (!actions.length || !record?.recordId) {
    return null;
  }

  return (
    <div
      className="mt-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3"
      data-testid="chat-prompt-actions"
    >
      <span className="w-full text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {t("Suggested actions")}
      </span>
      {actions.map((action, index) => (
        <button
          key={`${action.type}-${index}`}
          type="button"
          disabled={disabled}
          onClick={() => openManagingPromptAction(dispatch, record, action)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {iconForAction(action.type)}
          {action.label}
        </button>
      ))}
    </div>
  );
};

export default ChatPromptActions;
